import { useSession } from "@/admin/hooks/use-session";
import { useOrg } from "@/admin/hooks/use-org";
import { AppFooter } from "@/admin/components/app-footer";
import { useNavigate, useLocation } from "react-router";
import {
  ChevronDown,
  ChevronRight,
  LogOut,
  Plus,
  Package,
  Layers,
  Grid3x3,
  Wand2,
  Shield,
  ShieldCheck,
  Calculator,
  Link2,
  Users,
  KeyRound,
  Languages,
  Handshake,
  Building2,
  UserRound,
  FileText,
  GitBranch,
  Home,
  CreditCard,
  UserCog,
  ClipboardList,
  ClipboardCheck,
  Settings2,
  Mail,
  MapPin,
  Menu,
  X,
  Scale,
  Filter,
  HelpCircle,
  Database,
  Tag,
  UserX,
  Factory,
  ScrollText,
  Radio,
  Code2,
  CircleDollarSign,
} from "lucide-react";
import { Logo } from "@/shared/components/ui/logo";
import api, { setAuthToken, setLoggingOut, clearActiveOrgContext } from '@/admin/lib/api';
import { headlessLogout } from "@/shared/lib/auth-api";
import { clearPersistedCacheForCurrentPartition } from "@/admin/lib/query-persistence/setup";
import { useQueryClient } from "@tanstack/react-query";
import {
  type ReactNode,
  type ComponentType,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { FLAGS } from "@/admin/hooks/use-feature-flag";
import { usePermissions } from "@/admin/hooks/use-permissions";
import { useMyAssignedClaimsBadge } from "@/admin/hooks/use-claims";
import { useEntityTypeByKey } from "@/admin/hooks/use-entities";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";

/* ------------------------------------------------------------------ */
/* Flat dropdown nav items                                            */
/* ------------------------------------------------------------------ */

import { toastApiError } from '@/shared/lib/api-error'
import { toast } from 'sonner'
interface ManageItem {
  path: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  featureFlag?: string;
  // Optional permission gate. The item shows only when the user holds at
  // least one of these codenames (org admins / superusers pass via
  // usePermissions().can). Management/Settings items gate on a *manage*
  // capability, not a reference `.view`, so external dealer roles - which
  // hold some reference views for the registration flow - never see the
  // configuration surfaces.
  anyPermission?: string[];
}

interface ManageSection {
  key: string;
  label: string;
  items: ManageItem[];
  featureFlag?: string;
  // Optional permission gate on the whole section. When set, the section
  // (and all its items) stays hidden unless the user holds at least one of
  // these codenames. Org admins / superusers pass via usePermissions().can.
  anyPermission?: string[];
}

interface PrimaryNavLink {
  label: string;
  path: string;
  featureFlag?: string;
  // Optional permission gate on top of the feature flag. The link shows
  // only when the user holds at least one of these codenames (org admins
  // / superusers pass via usePermissions().can). Used to hide
  // specialist tabs (Lab, Exceptions) from roles that have no business
  // there even though the tenant-level flag is on.
  anyPermission?: string[];
}

interface QuickAction {
  label: string;
  path: string;
  featureFlag?: string;
  // Optional permission gate, same contract as PrimaryNavLink.anyPermission.
  // Without it a quick action offered a flow the actor cannot complete: "+
  // Claim" showed to dealer roles holding no claims codename.
  anyPermission?: string[];
}

function normalizePath(path: string) {
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
}

function matchesNavPath(currentPath: string, navPath: string) {
  const normalizedCurrentPath = normalizePath(currentPath);
  const normalizedNavPath = normalizePath(navPath);

  return (
    normalizedCurrentPath === normalizedNavPath ||
    normalizedCurrentPath.startsWith(`${normalizedNavPath}/`)
  );
}

function matchesNavQuery(currentSearch: string, navQuery: string) {
  const currentParams = new URLSearchParams(currentSearch);
  const navParams = new URLSearchParams(navQuery);

  return Array.from(navParams.entries()).every(
    ([key, value]) => currentParams.get(key) === value,
  );
}

function getActiveNavPath(
  links: PrimaryNavLink[],
  location: { pathname: string; search: string },
) {
  let activePath: string | null = null;
  let bestScore = -1;

  for (const link of links) {
    const [linkPath, linkQuery] = link.path.split('?');
    if (!matchesNavPath(location.pathname, linkPath)) {
      continue;
    }
    if (linkQuery && !matchesNavQuery(location.search, linkQuery)) {
      continue;
    }

    const score = linkPath.length + (linkQuery ? 1000 + linkQuery.length : 0);
    if (score > bestScore) {
      bestScore = score;
      activePath = link.path;
    }
  }

  return activePath;
}

/** Filter manage sections and their items by active feature flags.
 *  Superusers bypass all feature flag gates and see every section/item. */
function filterSectionsByFlags(
  sections: ManageSection[],
  flags?: Record<string, boolean>,
  isSuperuser?: boolean,
  can?: (codename: string) => boolean,
): (ManageSection & { items: ManageItem[] })[] {
  // Superusers bypass feature-flag gates; permission gates still resolve
  // through ``can`` (which returns true for them anyway).
  // Fail closed: an item that declares anyPermission stays hidden unless a
  // `can` checker is supplied AND grants at least one of the codenames.
  // Items with no anyPermission are always allowed.
  const passesPermission = (item: ManageItem) =>
    !item.anyPermission ||
    item.anyPermission.length === 0 ||
    (can ? item.anyPermission.some((p) => can(p)) : false);

  return sections.flatMap((section) => {
    if (
      !isSuperuser &&
      section.featureFlag &&
      flags?.[section.featureFlag] !== true
    )
      return [];
    // Section-level permission gate. Fail closed: a section that declares
    // anyPermission stays hidden unless `can` grants at least one codename.
    if (
      section.anyPermission &&
      section.anyPermission.length > 0 &&
      !(can && section.anyPermission.some((p) => can(p)))
    )
      return [];
    const items = section.items.filter((item) => {
      const flagOk =
        isSuperuser || !item.featureFlag || flags?.[item.featureFlag] === true;
      return flagOk && passesPermission(item);
    });
    if (items.length === 0) return [];
    return [{ ...section, items }];
  });
}

function ManageSections({
  onNavigate,
  sections,
  featureFlags,
  isSuperuser,
  can,
}: {
  onNavigate: (path: string) => void;
  sections: ManageSection[];
  featureFlags?: Record<string, boolean>;
  isSuperuser?: boolean;
  can?: (codename: string) => boolean;
}) {
  const [hovered, setHovered] = useState<string | null>(null);
  const [flyoutDirection, setFlyoutDirection] = useState<"left" | "right">(
    "right",
  );
  const triggerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const flyoutRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useLayoutEffect(() => {
    if (!hovered) {
      return;
    }

    const trigger = triggerRefs.current[hovered];
    const flyout = flyoutRefs.current[hovered];
    if (!trigger || !flyout) return;

    const frame = window.requestAnimationFrame(() => {
      const gap = 2;
      const triggerRect = trigger.getBoundingClientRect();
      const flyoutRect = flyout.getBoundingClientRect();
      const hasRoomOnRight =
        triggerRect.right + gap + flyoutRect.width <= window.innerWidth;
      const nextDirection = hasRoomOnRight ? "right" : "left";

      if (flyoutDirection !== nextDirection) {
        setFlyoutDirection(nextDirection);
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [flyoutDirection, hovered]);

  return (
    <div className="border-t-[0.5px] border-t-[var(--rule)] py-0.5">
      {filterSectionsByFlags(sections, featureFlags, isSuperuser, can).map((section) => {
        return (
          <div
            key={section.key}
            className="relative"
            ref={(node) => {
              triggerRefs.current[section.key] = node;
            }}
            onMouseEnter={() => {
              setFlyoutDirection("right");
              setHovered(section.key);
            }}
            onMouseLeave={() => setHovered(null)}
          >
            <Button
              type="button"
              variant="ghost"
              size="xs"
              className="h-auto w-full cursor-pointer justify-between rounded-none px-3 py-1 text-left text-base font-[350] text-[var(--ink-2)] hover:bg-[var(--bg-subtle)] hover:text-[var(--ink-2)]"
            >
              <span>{section.label}</span>
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
            </Button>

            {/* Flyout - to the right */}
            {hovered === section.key && (
              <div
                ref={(node) => {
                  flyoutRefs.current[section.key] = node;
                }}
                className={cn(
                  "cv-panel absolute top-0 z-[51] min-w-[160px] py-0.5",
                  flyoutDirection === "right"
                    ? "left-full pl-1 -ml-1"
                    : "right-full pr-1 -mr-1",
                )}
                onMouseEnter={() => setHovered(section.key)}
                onMouseLeave={() => setHovered(null)}
              >
                {section.items.map(({ path, label, icon: Icon }) => (
                  <Button
                    key={path}
                    type="button"
                    onClick={() => onNavigate(path)}
                    variant="ghost"
                    size="xs"
                    className="h-auto w-full cursor-pointer justify-start rounded-none px-3 py-1 text-left text-base font-[350] text-[var(--ink-2)] hover:bg-[var(--bg-subtle)] hover:text-[var(--ink)]"
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                    {label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* AppShell                                                           */
/* ------------------------------------------------------------------ */

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();

  const NAV_LINKS: PrimaryNavLink[] = [
    {
      label: t("nav.dashboard", { defaultValue: "Dashboard" }),
      path: "/dashboard",
    },
    {
      label: t("nav.registrations"),
      path: "/registrations",
      featureFlag: FLAGS.REGISTRATIONS,
    },
    {
      label: t("nav.claims", { defaultValue: "Claims" }),
      path: "/claims",
      featureFlag: FLAGS.CLAIMS,
      // The tenant flag says the module exists; it does not say this actor may
      // read a claim. Supagard's dealer role holds no claims codename at all,
      // so without this the tab rendered for every dealer and every panel on it
      // 403'd ("Couldn't load this list") - seven failed calls per page load.
      anyPermission: ["claims.view.global", "claims.view.acl"],
    },
    {
      label: "My Claims",
      path: "/claims?view=mine",
      featureFlag: FLAGS.CLAIMS,
      anyPermission: ["claims.view.global", "claims.view.acl"],
    },
    {
      label: "Lab",
      path: "/lab",
      featureFlag: FLAGS.LAB,
      // Lab queue is for lab technicians (and admins). Hidden from roles
      // with no lab-stage permission.
      anyPermission: ["claims.lab_actions"],
    },
    {
      label: t("nav.transfers", { defaultValue: "Transfers" }),
      path: "/transfers",
      featureFlag: FLAGS.TRANSFERS,
      // Transfers tab - any role with transfer visibility. Hidden from
      // roles with no transfer permission at all (Lab, Plant, Settlement,
      // Inspector, Coordinator, Inspection Lead, LOA Approver).
      anyPermission: ["transfers.view"],
    },
    {
      label: t("nav.inspections", { defaultValue: "Inspections" }),
      path: "/inspections",
      featureFlag: FLAGS.INSPECTIONS,
      // Inspections tab - any role with inspection view access.
      anyPermission: ["inspections.view.global", "inspections.view.acl"],
    },
    {
      label: t("nav.exceptions", { defaultValue: "Exceptions" }),
      path: "/registrations/exceptions",
      featureFlag: FLAGS.EXCEPTIONS,
      // Exceptions / approvals surface - only roles that view, manage, or
      // approve exceptions (incl. registration exceptions) see this tab.
      anyPermission: [
        "exceptions.view",
        "exceptions.manage",
        "exceptions.approve",
        "registrations.exceptions.approve",
      ],
    },
    {
      label: "HOA",
      path: "/hoa-communities",
      featureFlag: FLAGS.HOA,
    },
  ];

  const QUICK_ACTIONS: QuickAction[] = [
    {
      label: "Registration",
      path: "/registrations/new",
      featureFlag: FLAGS.REGISTRATIONS,
      anyPermission: ["holdings.create"],
    },
    {
      label: "Claim",
      path: "/claims/submit",
      featureFlag: FLAGS.CLAIMS,
      anyPermission: ["claims.view.global", "claims.view.acl"],
    },
  ];

  const { data: session } = useSession();
  const {
    orgName,
    orgLogoUrl,
    adminBranding,
    isPartnerAccess,
    activePartnershipUuid,
    leavePartnerContext,
    data: orgData,
  } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuShift, setMenuShift] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpandedSection, setMobileExpandedSection] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const user = session?.user;
  const isImpersonating = session?.is_impersonating ?? false;
  const featureFlags = orgData?.tenant_feature_flags;
  const { can } = usePermissions();
  const policiesTabEnabled = featureFlags?.[FLAGS.POLICIES_TAB] === true;
  const hasFeatureFlags = !!featureFlags;
  const claimsEnabled = featureFlags?.[FLAGS.CLAIMS] === true;
  // The flag says the module exists; `can` says this actor may read a claim.
  // Without the second half this probe 403'd for every dealer session.
  const { data: myClaimsData } = useMyAssignedClaimsBadge(
    claimsEnabled &&
      (can("claims.view.global") || can("claims.view.acl")),
  );
  const hasAssignedClaims = (myClaimsData?.count ?? 0) > 0;
  // Pricing (WPS-162) is entity-presence gated: the menu item shows only when
  // the tenant has the pricing entity, matching the Products tab + backend gate
  // (no feature flag, no tenant slug).
  const { data: pricingEntity } = useEntityTypeByKey("material_pricing_rate");
  const hasPricingEntity = !!pricingEntity;

  const DROPDOWN_SECTIONS: ManageSection[] = [
    {
      key: "products",
      label: t("manage.productsSection", { defaultValue: "Products" }),
      featureFlag: FLAGS.PRODUCTS,
      items: [
        {
          path: "/manage/products/library",
          label: t("manage.productLibrary", {
            defaultValue: "Product Library",
          }),
          icon: Package,
          anyPermission: [
            "products.create",
            "products.edit.global",
            "products.delete",
          ],
        },
        {
          path: "/manage/products/templates",
          label: t("manage.productTemplates", {
            defaultValue: "Product Templates",
          }),
          icon: Layers,
          anyPermission: [
            "product-templates.create",
            "product-templates.edit",
            "product-templates.delete",
          ],
        },
        {
          path: "/manage/products/host-templates",
          label: "Host Templates",
          icon: Home,
          anyPermission: [
            "host-templates.create",
            "host-templates.edit",
            "host-templates.delete",
          ],
        },
        {
          path: "/manage/products/transaction-templates",
          label: "Transactions",
          icon: CreditCard,
          anyPermission: [
            "products.create",
            "products.edit.global",
            "products.delete",
          ],
        },
        {
          path: "/manage/products/categories",
          label: t("manage.productCategories", {
            defaultValue: "Product Categories",
          }),
          icon: Grid3x3,
          anyPermission: [
            "product-categories.create",
            "product-categories.edit",
            "product-categories.delete",
          ],
        },
        {
          path: "/manage/products/brands",
          label: t("manage.productBrands", {
            defaultValue: "Product Brands",
          }),
          icon: Tag,
          featureFlag: FLAGS.BRANDS,
          anyPermission: [
            "product-brands.create",
            "product-brands.edit",
            "product-brands.delete",
          ],
        },
        {
          path: "/manage/products/filtering",
          label: t("manage.productFiltering", {
            defaultValue: "Rules",
          }),
          icon: Filter,
          anyPermission: ["rules.manage"],
        },
        ...(hasPricingEntity
          ? [
              {
                path: "/manage/products/pricing",
                label: t("manage.pricing", { defaultValue: "Pricing" }),
                icon: CircleDollarSign,
                anyPermission: [
                  "pricing.view",
                  "pricing.create",
                  "pricing.edit",
                  "pricing.delete",
                ],
              },
            ]
          : []),
        {
          path: "/manage/products/wizard",
          label: t("manage.productWizard", { defaultValue: "Product Wizard" }),
          icon: Wand2,
          anyPermission: ["products.create"],
        },
      ],
    },
    {
      // Geography (WPS-222): every geographic reference table in one place -
      // zones, plants, PSA mappings, postal codes, availability and the zone
      // ownership claims route on. Gated on the geo capability; the section
      // shows when the user can read either the reference data or ownership.
      key: "geography",
      label: t("manage.geographySection", { defaultValue: "Geography" }),
      featureFlag: FLAGS.GEOGRAPHIC_AVAILABILITY,
      anyPermission: ["geo-zones.view", "geo-assignments.view"],
      items: [
        {
          path: "/manage/geography/zones",
          label: t("manage.geographyZones", { defaultValue: "Zones" }),
          icon: MapPin,
          anyPermission: ["geo-zones.view"],
        },
        {
          path: "/manage/geography/plants",
          label: t("manage.geographyPlants", { defaultValue: "Plants" }),
          icon: Factory,
          anyPermission: ["geo-zones.view"],
        },
        {
          path: "/manage/geography/postal-codes",
          label: t("manage.geographyPostalCodes", {
            defaultValue: "Postal Codes",
          }),
          icon: MapPin,
          anyPermission: ["geo-zones.view"],
        },
        {
          path: "/manage/geography/assignments",
          label: t("manage.geographyAssignments", {
            defaultValue: "Assignments",
          }),
          icon: Users,
          anyPermission: ["geo-assignments.view"],
        },
      ],
    },
    {
      key: "warranties",
      label: t("manage.guaranteesSection", { defaultValue: "Warranties" }),
      items: [
        {
          path: "/manage/warranties/templates",
          label: t("manage.warrantyClasses", {
            defaultValue: "Warranty Templates",
          }),
          icon: Shield,
          anyPermission: [
            "warranty-templates.create",
            "warranty-templates.edit",
            "warranty-templates.delete",
          ],
        },
        ...(policiesTabEnabled
          ? [
              {
                path: "/manage/warranties/policies",
                label: t("manage.warrantyPolicies", {
                  defaultValue: "Policies",
                }),
                icon: ShieldCheck,
                anyPermission: [
                  "warranty-templates.create",
                  "warranty-templates.edit",
                  "warranty-templates.delete",
                ],
              },
            ]
          : hasFeatureFlags
            ? [
                {
                  path: "/manage/warranties/mappings",
                  label: t("manage.warrantyMappings", {
                    defaultValue: "Warranty Mappings",
                  }),
                  icon: Link2,
                  anyPermission: [
                    "warranty-mappings.create",
                    "warranty-mappings.edit",
                    "warranty-mappings.delete",
                  ],
                },
              ]
            : []),
        {
          path: "/manage/warranties/filtering",
          label: t("manage.warrantyFiltering", {
            defaultValue: "Rules",
          }),
          icon: Filter,
          anyPermission: ["rules.manage"],
        },
        {
          path: "/manage/warranties/terms",
          label: t("manage.warrantyTerms", {
            defaultValue: "Terms & Conditions",
          }),
          icon: Scale,
          anyPermission: [
            "warranty-terms.create",
            "warranty-terms.edit",
            "warranty-terms.delete",
          ],
        },
        {
          path: "/manage/warranties/proration",
          label: t("manage.prorationFormulas", {
            defaultValue: "Proration Formulas",
          }),
          icon: Calculator,
          featureFlag: FLAGS.PRORATION_FORMULAS,
          anyPermission: [
            "proration-formulas.create",
            "proration-formulas.edit",
            "proration-formulas.delete",
          ],
        },
      ],
    },
    {
      key: "claims",
      label: "Claims",
      featureFlag: FLAGS.CLAIMS,
      items: [
        {
          path: "/manage/claims/schemas",
          label: "Claim Schemas",
          icon: FileText,
          anyPermission: [
            "claim-schemas.create",
            "claim-schemas.edit",
            "claim-schemas.delete",
          ],
        },
        {
          path: "/manage/claims/workflows",
          label: "Claim Workflows",
          icon: GitBranch,
          anyPermission: [
            "claim-workflows.create",
            "claim-workflows.edit",
            "claim-workflows.delete",
          ],
        },
        {
          path: "/manage/claims/mappings",
          label: "Claim Mappings",
          icon: Link2,
          anyPermission: [
            "claim-mappings.create",
            "claim-mappings.edit",
            "claim-mappings.delete",
          ],
        },
      ],
    },
    {
      key: "network",
      label: "Network",
      items: [
        {
          path: "/network/partners",
          label: "Partners",
          icon: Building2,
          anyPermission: ["partnerships.view"],
        },
        {
          path: "/network/people",
          label: "People",
          icon: Users,
          // Tenant-admin only for now. users.manage is a floor codename, so no
          // partner role can ever hold it, and org admins pass automatically.
          anyPermission: ["users.manage"],
        },
        {
          path: "/network/customers",
          label: "Customers",
          icon: UserRound,
          anyPermission: ["customers.view"],
        },
        {
          path: "/network/external-roles",
          label: "External Roles",
          icon: UserCog,
          anyPermission: ["roles.manage"],
        },
      ],
    },
    {
      key: "settings",
      label: t("nav.settings", { defaultValue: "Settings" }),
      // The whole Settings area is gated by a single coarse capability.
      // Only admin-tier roles hold ``settings.view`` (org admins / staff pass
      // via can); every other role never sees the Settings section. The
      // ``/settings`` route enforces the same gate (see admin/App.tsx).
      anyPermission: ["settings.view"],
      items: [
        // These mirror the Settings sidebar nav (settings-page.tsx) so the
        // dropdown and the in-settings sidebar stay consistent: gating is by
        // feature flag (+ the same exceptions/plants permission checks the
        // sidebar uses), not by separate per-item codenames. Keep this list
        // in sync with the sidebar groups. Route-level ProtectedRoute is the
        // real access gate.
        {
          path: "/settings/organisation",
          label: "Organisation",
          icon: Building2,
        },
        {
          path: "/settings/platform",
          label: "Platform",
          icon: Settings2,
        },
        {
          path: "/settings/users",
          label: t("settings.users", { defaultValue: "Users" }),
          icon: Users,
        },
        {
          path: "/settings/roles",
          label: t("settings.roles", { defaultValue: "Roles" }),
          icon: Shield,
        },
        {
          path: "/settings/mfa",
          label: t("settings.mfa", { defaultValue: "Security" }),
          icon: ShieldCheck,
        },
        {
          path: "/settings/registration",
          label: "Registration",
          icon: ClipboardList,
        },
        {
          path: "/settings/claims",
          label: "Claims",
          icon: ClipboardCheck,
          featureFlag: FLAGS.CLAIMS,
        },
        {
          path: "/settings/exceptions",
          label: "Exceptions",
          icon: Scale,
          featureFlag: FLAGS.EXCEPTIONS,
          anyPermission: ["exceptions.view", "exceptions.manage"],
        },
        {
          path: "/settings/translations",
          label: t("settings.translations", { defaultValue: "Translations" }),
          icon: Languages,
        },
        {
          path: "/settings/email-templates",
          label: "Email Templates",
          icon: Mail,
          featureFlag: FLAGS.EMAIL_AUTHORING,
        },
        {
          path: "/settings/email-triggers",
          label: t("settings.emailTriggers", { defaultValue: "Email Triggers" }),
          icon: Filter,
          featureFlag: FLAGS.EMAIL_TRIGGERS,
        },
        {
          path: "/settings/integrations",
          label: t("settings.integrations", { defaultValue: "Integrations" }),
          icon: Link2,
          featureFlag: FLAGS.INTEGRATIONS,
        },
        {
          path: "/settings/api-keys",
          label: t("settings.apiKeys", { defaultValue: "API Keys" }),
          icon: KeyRound,
        },
        {
          path: "/settings/api-logs",
          label: t("settings.apiLogs", { defaultValue: "API Logs" }),
          icon: ScrollText,
        },
        {
          path: "/settings/webhooks",
          label: t("settings.webhooks", { defaultValue: "Webhooks" }),
          icon: Radio,
          featureFlag: FLAGS.PULSE_WEBHOOKS,
        },
        {
          path: "/admin/developers",
          label: t("settings.developerPortal", { defaultValue: "Developer Portal" }),
          icon: Code2,
          featureFlag: FLAGS.PUBLIC_API,
        },
        {
          path: "/manage/workflows",
          label: "Workflow Editor",
          icon: GitBranch,
          featureFlag: FLAGS.WORKFLOW_DESIGNER,
          anyPermission: ["workflows.admin_override"],
        },
        {
          path: "/manage/workflows/entities",
          label: "Entity Editor",
          icon: Database,
          featureFlag: FLAGS.WORKFLOW_DESIGNER,
          anyPermission: ["entities.manage"],
        },
      ],
    },
  ];

  // Partner (dealer) context: partners are internal-config outsiders, so the
  // dropdown is scoped to the Network section only - no Products /
  // Warranties / Claims / Settings management. The Network section collapses
  // to the dealer's OWN partnership detail page (they have exactly one
  // partnership and are scoped out of the partner book / external roles).
  const dropdownSections: ManageSection[] = isPartnerAccess
    ? DROPDOWN_SECTIONS.filter((section) => section.key === "network").map(
        (section) =>
          activePartnershipUuid
            ? {
                ...section,
                items: [
                  {
                    path: `/network/${activePartnershipUuid}`,
                    label: orgName
                      ? `${orgName} Partnership`
                      : "My Partnership",
                    icon: Building2,
                    anyPermission: ["partnerships.view"],
                  },
                ],
              }
            : section,
      )
    : DROPDOWN_SECTIONS;

  const visibleNavLinks = NAV_LINKS.filter(
    (link) =>
      (!link.featureFlag || featureFlags?.[link.featureFlag] === true) &&
      (!link.anyPermission || link.anyPermission.some((p) => can(p))) &&
      (link.path !== '/claims?view=mine' || hasAssignedClaims),
  );
  const visibleQuickActions = QUICK_ACTIONS.filter(
    (action) =>
      (!action.featureFlag || featureFlags?.[action.featureFlag] === true) &&
      (!action.anyPermission || action.anyPermission.some((p) => can(p))),
  );
  const activeNavPath = getActiveNavPath(visibleNavLinks, location);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [mobileMenuOpen])

  // Close menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setMenuShift(0);
      }
    }
    if (menuOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  useLayoutEffect(() => {
    if (!menuOpen) {
      return;
    }

    function updateMenuShift() {
      const dropdown = dropdownRef.current;
      if (!dropdown) return;

      const viewportPadding = 8;
      const rect = dropdown.getBoundingClientRect();
      const overflowRight = rect.right - (window.innerWidth - viewportPadding);
      let nextShift = overflowRight > 0 ? -overflowRight : 0;

      if (rect.left + nextShift < viewportPadding) {
        nextShift += viewportPadding - (rect.left + nextShift);
      }

      setMenuShift((currentShift) =>
        currentShift === nextShift ? currentShift : nextShift,
      );
    }

    const frame = window.requestAnimationFrame(updateMenuShift);
    window.addEventListener("resize", updateMenuShift);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateMenuShift);
    };
  }, [menuOpen]);

  async function handleLogout() {
    setMenuOpen(false);
    setMenuShift(0);
    try {
      setLoggingOut(true);
      // Drop this session's persisted (IndexedDB) cache before the token/context
      // are cleared, so no PII-shaped rows linger on disk after logout.
      await clearPersistedCacheForCurrentPartition();
      // Revokes the session server-side (deletes the session row, killing the
      // token) and clears the local token store.
      await headlessLogout();
      // Clear all cached queries to prevent 401 refetch races
      queryClient.clear();
      const slug = sessionStorage.getItem("tenantSlug");
      sessionStorage.removeItem("tenantSlug");
      sessionStorage.removeItem("activeOrganisationUuid");
      sessionStorage.removeItem("activePartnerUuid");
      navigate(slug ? `/${slug}/login` : "/login");
    } catch (err) {
      toastApiError(err, "Logout failed");
    } finally {
      setLoggingOut(false);
    }
  }

  async function handleReleaseImpersonation() {
    setMenuOpen(false);
    setMenuShift(0);
    try {
      const res = await api.post("/staff/impersonate/release/");
      if (res.data.session_token) setAuthToken(res.data.session_token);
      toast.success("Impersonation released");
      // Drop the impersonated dealer's partner selection before the
      // staff session resumes, or staff return in partner context.
      clearActiveOrgContext();
      window.location.href = "/staff/dashboard";
    } catch (err) {
      toastApiError(err, "Failed to release impersonation");
    }
  }

  function handleManageNavigate(path: string) {
    setMenuOpen(false);
    setMenuShift(0);
    navigate(path);
  }

  function handleMobileNavigate(path: string) {
    setMobileMenuOpen(false)
    setMobileExpandedSection(null)
    navigate(path)
  }

  const partnerDownstreamName = orgData?.partnerships?.find(
    (p) =>
      p.downstream_organisation.uuid ===
      orgData?.active_downstream_organisation_uuid,
  )?.downstream_organisation.name;

  function handleReturnToLaunchpad() {
    leavePartnerContext();
    navigate("/dashboard");
  }

  return (
    <div className="flex min-h-screen flex-col bg-[var(--bg)]">
      {/* Partner context banner */}
      {isPartnerAccess && (
        <div className="flex items-center justify-between border-b-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] px-6 py-1.5">
          <div className="flex items-center gap-2">
            <Handshake className="h-3 w-3 text-[var(--green)]" />
            <span className="text-sm text-[var(--green)]">
              Operating as <strong>{partnerDownstreamName ?? "Partner"}</strong>{" "}
              in {orgName}
            </span>
          </div>
          <button
            onClick={handleReturnToLaunchpad}
            className="cursor-pointer rounded-[3px] border-[0.5px] border-[var(--green-border)] px-2 py-0.5 text-xs uppercase tracking-[0.1em] text-[var(--green)] transition-colors hover:bg-[var(--green)] hover:text-[var(--bg)]"
          >
            {t("partner.switchAccount", { defaultValue: "Switch account" })}
          </button>
        </div>
      )}
      <header className="w-full border-b-[0.5px] border-[var(--rule)] bg-[var(--bg)]">
        <div className="page-container h-14 flex items-center justify-between">
          {/* Left: branding + nav */}
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 cursor-pointer -translate-y-[2px]"
            >
              {adminBranding === 'BRANDED' && orgLogoUrl ? (
                <img
                  src={orgLogoUrl}
                  alt={orgName ?? ''}
                  className="h-6 max-w-[140px] object-contain"
                />
              ) : (
                <Logo size="sm" />
              )}
            </button>

            <nav className="hidden lg:flex items-center gap-7">
              {visibleNavLinks.map((link) => {
                const isActive = activeNavPath === link.path;
                return (
                  <button
                    key={link.path}
                    onClick={() => navigate(link.path)}
                    className={cn(
                      "cursor-pointer border-0 border-b-[0.5px] py-0.5 text-sm uppercase tracking-[0.12em] transition-all duration-150",
                      isActive
                        ? "border-b-[var(--ink)] font-normal text-[var(--ink)]"
                        : "border-b-transparent text-[var(--ink-3)] hover:text-[var(--ink)]",
                    )}
                  >
                    {link.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Right: actions + org dropdown */}
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => navigate("/guide")}
              className="hidden lg:inline-flex cursor-pointer text-[var(--ink-3)] hover:text-[var(--ink)]"
              title="Platform Guide"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
            {visibleQuickActions.length > 0 && (
              <div className="hidden lg:flex items-center gap-1.5">
                {visibleQuickActions.map((action) => (
                  <Button
                    key={action.path}
                    onClick={() => navigate(action.path)}
                    variant="outline"
                    className="cursor-pointer items-center gap-1 rounded-md border-[0.5px] border-[var(--rule-strong)] bg-transparent px-2.5 py-1 text-xs uppercase tracking-[0.1em] text-[var(--ink-2)] transition-colors duration-150 hover:border-[var(--ink-3)] inline-flex"
                  >
                    <Plus className="h-2.5 w-2.5" />
                    {action.label}
                  </Button>
                ))}
              </div>
            )}

            <div className="relative" ref={menuRef}>
              <Button
                type="button"
                onClick={() =>
                  setMenuOpen((currentOpen) => {
                    const nextOpen = !currentOpen;
                    if (!nextOpen) {
                      setMenuShift(0);
                    }
                    return nextOpen;
                  })
                }
                variant="ghost"
                size="xs"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                data-testid="org-menu-trigger"
                className={cn(
                  "h-auto cursor-pointer rounded-[4px] px-2.5 py-1 text-[var(--ink-2)] transition-all duration-150",
                  menuOpen
                    ? "bg-[var(--bg-subtle)]"
                    : "hover:bg-[var(--bg-subtle)]",
                )}
              >
                {orgLogoUrl ? (
                  <img
                    src={orgLogoUrl}
                    alt={orgName ?? ""}
                    className="h-7 max-w-[140px] object-contain"
                  />
                ) : (
                  <span className="text-sm font-normal uppercase tracking-[0.12em]">
                    {orgName ?? t("nav.organisation")}
                  </span>
                )}
                <ChevronDown
                  className={cn(
                    "h-3 w-3 transition-transform duration-150",
                    menuOpen && "rotate-180",
                  )}
                />
              </Button>

              {/* Dropdown - StreamCOC style */}
              {menuOpen && (
                <div
                  ref={dropdownRef}
                  className="cv-panel absolute left-0 top-full z-50 mt-1 min-w-[170px] py-1"
                  style={{
                    transform: `translateX(${menuShift}px)`,
                  }}
                >
                  {/* User info */}
                  {user && (
                    <div className="border-b-[0.5px] border-[var(--rule)] px-3 py-2">
                      <p className="text-base font-[350] text-[var(--ink)]">
                        {user.first_name} {user.last_name}
                      </p>
                      <p className="mt-0.5 text-base font-[350] text-[var(--ink-3)]">
                        {user.email}
                      </p>
                      {orgName && (
                        <p className="mt-0.5 text-base font-[350] text-[var(--green)]">
                          {orgName}
                        </p>
                      )}
                    </div>
                  )}

                  <ManageSections
                    onNavigate={handleManageNavigate}
                    sections={dropdownSections}
                    featureFlags={orgData?.tenant_feature_flags}
                    isSuperuser={user?.is_superuser}
                    can={can}
                  />

                  {/* Release impersonation */}
                  {isImpersonating && (
                    <div className="border-t-[0.5px] border-t-[var(--rule)] py-0.5">
                      <Button
                        type="button"
                        onClick={handleReleaseImpersonation}
                        variant="ghost"
                        size="xs"
                        className="h-auto w-full cursor-pointer justify-start rounded-none px-3 py-2 text-left text-base font-[350] text-[var(--status-warn)] hover:bg-[var(--status-warn-bg)] hover:text-[var(--status-warn)]"
                      >
                        <UserX className="h-3.5 w-3.5 shrink-0" />
                        Stop impersonating
                      </Button>
                    </div>
                  )}

                  {/* Sign out */}
                  <div className="border-t-[0.5px] border-t-[var(--rule)] py-0.5">
                    <Button
                      type="button"
                      onClick={handleLogout}
                      variant="ghost"
                      size="xs"
                      className="h-auto w-full cursor-pointer justify-start rounded-none px-3 py-2 text-left text-base font-[350] text-[var(--ink-2)] hover:bg-[var(--bg-subtle)] hover:text-[var(--ink-2)]"
                    >
                      <LogOut className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                      {t("common.signOut")}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile hamburger button - inside the right group so justify-between
                keeps it adjacent to the tenant logo on narrow widths. */}
            <button
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="lg:hidden flex items-center justify-center h-8 w-8 rounded-[4px] cursor-pointer transition-colors"
              style={{ color: 'var(--ink-2)' }}
            >
              {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </header>
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 top-14 z-40 overflow-y-auto"
          style={{ background: 'var(--bg)' }}
        >
          <div className="page-container py-4">
            {/* User info */}
            {user && (
              <div className="pb-3 mb-3" style={{ borderBottom: '0.5px solid var(--rule)' }}>
                <p className="text-base text-[var(--ink)]">
                  {user.first_name} {user.last_name}
                </p>
                <p className="text-sm text-[var(--ink-3)]">
                  {user.email}
                </p>
                {orgName && (
                  <p className="mt-0.5 text-sm text-[var(--green)]">
                    {orgName}
                  </p>
                )}
              </div>
            )}

            {/* Nav links */}
            <div className="pb-3 mb-3" style={{ borderBottom: '0.5px solid var(--rule)' }}>
              {visibleNavLinks.map((link) => {
                const isActive = activeNavPath === link.path
                return (
                  <button
                    key={link.path}
                    onClick={() => handleMobileNavigate(link.path)}
                    className="flex w-full items-center py-2.5 text-base uppercase tracking-[0.1em] cursor-pointer"
                    style={{
                      color: isActive ? 'var(--ink)' : 'var(--ink-2)',
                      fontWeight: isActive ? 400 : 300,
                    }}
                  >
                    {link.label}
                  </button>
                )
              })}
            </div>

            {/* Quick actions */}
            {visibleQuickActions.length > 0 && (
              <div className="flex gap-2 pb-3 mb-3" style={{ borderBottom: '0.5px solid var(--rule)' }}>
                {visibleQuickActions.map((action) => (
                  <button
                    key={action.path}
                    onClick={() => handleMobileNavigate(action.path)}
                    className="flex-1 inline-flex items-center justify-center gap-1 rounded-[6px] px-3 py-2 text-xs uppercase tracking-[0.1em] cursor-pointer"
                    style={{
                      color: 'var(--ink-2)',
                      border: '0.5px solid var(--rule-strong)',
                    }}
                  >
                    <Plus className="h-3 w-3" />
                    {action.label}
                  </button>
                ))}
              </div>
            )}

            {/* Manage sections - accordion style on mobile */}
            <div className="pb-3 mb-3" style={{ borderBottom: '0.5px solid var(--rule)' }}>
              {filterSectionsByFlags(dropdownSections, orgData?.tenant_feature_flags, user?.is_superuser, can).map((section) => {
                return (
                  <div key={section.key}>
                    <button
                      onClick={() => setMobileExpandedSection(
                        mobileExpandedSection === section.key ? null : section.key
                      )}
                      className="flex w-full items-center justify-between py-2.5 text-sm uppercase tracking-[0.1em] cursor-pointer"
                      style={{ color: 'var(--ink-2)' }}
                    >
                      <span>{section.label}</span>
                      <ChevronDown
                        className="h-3 w-3 transition-transform duration-150"
                        style={{
                          color: 'var(--ink-3)',
                          transform: mobileExpandedSection === section.key ? 'rotate(180deg)' : undefined,
                        }}
                      />
                    </button>
                    {mobileExpandedSection === section.key && (
                      <div className="pl-2 pb-2">
                        {section.items.map(({ path, label, icon: Icon }) => (
                          <button
                            key={path}
                            onClick={() => handleMobileNavigate(path)}
                            className="flex w-full items-center gap-2 py-2 text-sm cursor-pointer"
                            style={{ color: 'var(--ink-2)' }}
                          >
                            <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--ink-3)' }} />
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Release impersonation */}
            {isImpersonating && (
              <Button
                type="button"
                onClick={handleReleaseImpersonation}
                variant="ghost"
                size="xs"
                className="h-auto w-full cursor-pointer justify-start rounded-none px-0 py-2.5 text-left text-sm uppercase tracking-[0.1em] text-[var(--status-warn)] hover:text-[var(--status-warn)]"
              >
                <UserX className="h-3.5 w-3.5 shrink-0" />
                Stop impersonating
              </Button>
            )}

            {/* Sign out */}
            <Button
              type="button"
              onClick={handleLogout}
              variant="ghost"
              size="xs"
              className="h-auto w-full cursor-pointer justify-start rounded-none px-0 py-2.5 text-left text-sm uppercase tracking-[0.1em] text-[var(--ink-2)] hover:text-[var(--ink-2)]"
            >
              <LogOut className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
              {t('common.signOut')}
            </Button>
          </div>
        </div>
      )}
      {children}
      <AppFooter />
    </div>
  );
}
